/**
 * Section B — "Not sad, just flat" (anhedonic depression).
 * Placed directly after the existing "What is Prism?" section.
 *
 * GUARDRAIL: The only FDA claim allowed anywhere is that Prism is FDA-cleared
 * as an adjunctive treatment for symptoms associated with PTSD. The depression
 * protocol is NOT FDA-cleared for depression; the disclosure box states this.
 */
const looksLike = [
  "Things you used to love feel like chores",
  "Getting started on anything takes enormous effort",
  "Being tired all the time without a clear reason",
  "Feeling emotionally muted, even around people you care about",
  "\u201CI can function, but I'm not living\u201D",
  "Life happening around you instead of with you",
];

export default function AnhedonicDepressionSection() {
  return (
    <section
      className="relative bg-light-blue/40 py-16 lg:py-24"
      aria-labelledby="prism-anhedonia-heading"
    >
      <div className="container-px mx-auto max-w-3xl">
        <span className="text-sm font-semibold uppercase tracking-widest text-accent">
          Anhedonic depression
        </span>
        <h2
          id="prism-anhedonia-heading"
          className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]"
        >
          You can function. You just can&rsquo;t feel much of anything.
        </h2>
        <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
          <p>
            A lot of people don&rsquo;t recognize themselves in the word
            &ldquo;depression&rdquo; because they aren&rsquo;t sad.
            They&rsquo;re muted. Work gets done and life keeps moving, but the
            color has gone out of it. Clinicians call this anhedonia: a loss of
            the ability to feel interest, motivation or pleasure.
          </p>
        </div>

        <h3 className="mt-8 font-headings text-lg font-semibold text-primary">
          It can look like:
        </h3>
        <ul className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
          {looksLike.map((item) => (
            <li
              key={item}
              className="flex gap-2.5 text-[15px] leading-relaxed text-muted-foreground"
            >
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h3 className="mt-8 font-headings text-lg font-semibold text-primary">
          Where Prism fits
        </h3>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Alongside the FDA-cleared PTSD protocol, Prism now offers a depression
          protocol designed for people whose depression centers on anhedonia.
          Instead of targeting the brain&rsquo;s alarm system, it uses a marker
          linked to the brain&rsquo;s reward system, the circuitry behind
          motivation and pleasure. Sessions look much the same: an EEG cap, a
          screen that responds to your brain activity, and practice finding the
          mental strategies that move it. In a pilot study published in{" "}
          <em>Brain Sciences</em>, most participants showed a reduction in
          depressive symptoms.
        </p>

        <div
          className="mt-8 rounded-2xl border border-border bg-light-blue/30 p-5 text-sm leading-relaxed text-muted-foreground sm:p-6"
          role="note"
        >
          <p>
            Prism&rsquo;s FDA clearance is for adjunctive treatment of symptoms
            associated with PTSD. The depression protocol is not FDA-cleared for
            the treatment of depression and is used under the direction of your
            clinician as part of a broader treatment plan. Research on this
            protocol is ongoing.
          </p>
        </div>
      </div>
    </section>
  );
}
